// Detalle de una ficha: datos completos, cambio de estado (oficina) y
// (futuro) galería de archivos de OneDrive.
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ESTADO_FICHA, type EstadoFicha } from '@dim/shared';
import { useAuth } from '../auth.js';
import { api, ESTADO_LABEL, fechaCorta, type FichaDetalle as Ficha, ApiError } from '../api.js';

function Campo({ label, value }: { label: string; value: ReactNode }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="kv">
      <span className="k">{label}</span>
      <span className="v">{value}</span>
    </div>
  );
}

export function FichaDetalle() {
  const { id } = useParams();
  const { user } = useAuth();
  const [ficha, setFicha] = useState<Ficha | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nuevoEstado, setNuevoEstado] = useState<EstadoFicha>('recibida');
  const [guardando, setGuardando] = useState(false);

  const esOficina = user?.rol !== 'tecnico';

  async function cargar() {
    setLoading(true);
    setError('');
    try {
      const r = await api.getFicha(Number(id));
      setFicha(r.ficha);
      setNuevoEstado(r.ficha.estado === 'borrador' ? 'recibida' : r.ficha.estado);
    } catch (err) {
      setError(err instanceof ApiError && err.status === 404 ? 'Ficha no encontrada.' : 'Error al cargar.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function guardarEstado() {
    if (!ficha) return;
    setGuardando(true);
    try {
      await api.setEstado(ficha.id, nuevoEstado);
      await cargar();
    } catch {
      setError('No se pudo cambiar el estado.');
    } finally {
      setGuardando(false);
    }
  }

  if (loading) return <p className="muted">Cargando…</p>;
  if (error || !ficha) {
    return (
      <section>
        <p className="login-error">{error || 'Ficha no encontrada.'}</p>
        <Link to="/fichas">← Volver al listado</Link>
      </section>
    );
  }

  const d = ficha.data;
  const c = d.cliente;

  return (
    <section className="detalle">
      <Link to="/fichas" className="back">
        ← Listado
      </Link>

      <div className="page-head">
        <h1>{c.nombre}</h1>
        <span className={`badge estado-${ficha.estado}`}>{ESTADO_LABEL[ficha.estado]}</span>
      </div>
      <p className="muted">
        Técnico: {ficha.tecnicoNombre} · Creada {fechaCorta(ficha.createdAt)} · Actualizada{' '}
        {fechaCorta(ficha.updatedAt)}
      </p>

      {esOficina && (
        <div className="estado-bar">
          <label>Cambiar estado:</label>
          <select value={nuevoEstado} onChange={(e) => setNuevoEstado(e.target.value as EstadoFicha)}>
            {ESTADO_FICHA.filter((s) => s !== 'borrador').map((s) => (
              <option key={s} value={s}>
                {ESTADO_LABEL[s]}
              </option>
            ))}
          </select>
          <button className="btn-primary" onClick={() => void guardarEstado()} disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      )}

      <div className="card">
        <h2>Cliente y agua</h2>
        <div className="kvgrid">
          <Campo label="Nombre" value={c.nombre} />
          <Campo label="Teléfono" value={c.telefono} />
          <Campo label="Residencia" value={c.tipoResidencia} />
          <Campo label="Planta" value={c.planta} />
          <Campo label="Nº personas" value={c.nPersonas} />
          <Campo label="PE" value={c.pe} />
          <Campo label="PD" value={c.pd} />
          <Campo label="GA" value={c.ga} />
          <Campo label="TH (dureza)" value={c.th} />
          <Campo label="TDS" value={c.tds} />
          <Campo label="pH" value={c.ph} />
          <Campo label="Mascota" value={c.mascota} />
          <Campo label="Piel atópica" value={c.pielAtopica} />
        </div>
      </div>

      {d.descalcificador.presente && (
        <div className="card">
          <h2>Descalcificador</h2>
          <div className="kvgrid">
            <Campo label="Equipo" value={d.descalcificador.equipo} />
            <Campo label="Nº registros" value={d.descalcificador.nRegistros} />
            <Campo label="Dist. luz (m)" value={d.descalcificador.distanciaLuzM} />
            <Campo label="Dist. desagüe (m)" value={d.descalcificador.distanciaDesagueM} />
            <Campo label="Pasar paredes" value={d.descalcificador.pasarParedes} />
            <Campo label="Llave paso prefiltro" value={d.descalcificador.llavePasoPrefiltro} />
            <Campo label="Tiempo instalación" value={d.descalcificador.tiempoInstalacion} />
          </div>
          <Campo label="Descripción" value={d.descalcificador.descripcionInstalacion} />
        </div>
      )}

      {d.osmosis.presente && (
        <div className="card">
          <h2>Ósmosis</h2>
          <div className="kvgrid">
            <Campo label="Equipo" value={d.osmosis.equipo} />
            <Campo label="Dist. luz (m)" value={d.osmosis.distanciaLuzM} />
            <Campo label="Dist. desagüe (m)" value={d.osmosis.distanciaDesagueM} />
            <Campo label="Tiempo instalación" value={d.osmosis.tiempoInstalacion} />
          </div>
          <Campo label="Descripción" value={d.osmosis.descripcionInstalacion} />
        </div>
      )}

      <div className="card">
        <h2>Otros</h2>
        <div className="kvgrid">
          <Campo label="Regulador de presión" value={d.otros.reguladorPresion} />
          <Campo label="Prefiltro sedimentos" value={d.otros.prefiltroSedimentos} />
          <Campo label="Vaso de expansión" value={d.otros.vasoExpansion} />
        </div>
      </div>

      {d.notas && (
        <div className="card">
          <h2>Notas del técnico</h2>
          <p>{d.notas}</p>
        </div>
      )}

      <div className="card">
        <h2>Archivos ({ficha.archivos.length})</h2>
        {ficha.archivos.length === 0 ? (
          <p className="muted">
            Sin archivos todavía. La subida de fotos/vídeos a OneDrive llega en la Fase 4.
          </p>
        ) : (
          <ul className="archivos">
            {ficha.archivos.map((a) => (
              <li key={a.id}>
                {a.onedriveDownloadUrl ? (
                  <a href={a.onedriveDownloadUrl} target="_blank" rel="noreferrer">
                    {a.filename}
                  </a>
                ) : (
                  a.filename
                )}{' '}
                <span className={`badge archivo-${a.estado}`}>{a.estado}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
